import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import type { AppDispatch } from 'store';
import { setIsPrinting } from 'store/exportData.slice';

export interface AnalyticsReportExportParams {
  startDate: Date;
  endDate: Date;
  range: string;
  aggregation: string;
  shiftWiseAnalysis: boolean;
  shiftNumber: number;
  assetTypeValue: number; // 0 = all, 1 = haul_truck, 2 = loader
  utilizationType: string;
  dispatch: AppDispatch;
  fileNamePrefix?: string;
  returnType: 'excel' | 'pdf';
  dailyProductiveHoursRange?: string;
  suggestionData?: Record<string, unknown>;
  getReportBytes: (
    formattedStartDate: string,
    formattedEndDate: string,
    range: string,
    aggregation: string,
    shiftWiseAnalysis: boolean,
    shiftNumber: number,
    assetType: 'all' | 'hauler' | 'loader',
    utilizationType: string,
    returnType: 'excel' | 'pdf',
    dailyProductiveHoursRange?: string,
    suggestionData?: Record<string, unknown>
  ) => Promise<ArrayBuffer>;
  errorMessage?: string; // Optional custom error message
}

/**
 * Exports analytics report (fuel analytics, utilization, etc.) as PDF
 * @param params - Export parameters including a function to fetch report bytes
 * @returns Promise that resolves when export is complete
 */
export const exportAnalyticsReport = async (
  params: AnalyticsReportExportParams
): Promise<void> => {
  const {
    startDate,
    endDate,
    range,
    aggregation,
    shiftWiseAnalysis,
    shiftNumber,
    assetTypeValue,
    utilizationType,
    dispatch,
    fileNamePrefix = 'Report',
    dailyProductiveHoursRange,
    getReportBytes,
    errorMessage = 'Error calling analytics report API',
    returnType,
    suggestionData,
  } = params;

  dispatch(setIsPrinting(true));

  try {
    const formattedStartDate = dayjs(startDate).format('YYYY-MM-DD');
    const formattedEndDate = dayjs(endDate).format('YYYY-MM-DD');
    const assetType =
      assetTypeValue === 0 ? 'all' : assetTypeValue === 1 ? 'hauler' : 'loader';

    const aggregationValue = ['1Y', 'YTD', 'Custom'].includes(range)
      ? aggregation.toLowerCase()
      : 'daily';

    const bytes = await getReportBytes(
      formattedStartDate,
      formattedEndDate,
      range,
      aggregationValue,
      shiftWiseAnalysis,
      shiftNumber,
      assetType,
      utilizationType,
      returnType,
      dailyProductiveHoursRange ?? '7D',
      suggestionData
    );

    const fileExtension = returnType === 'excel' ? 'xlsx' : 'pdf';
    const blob = new Blob([bytes], {
      type:
        returnType === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
    });

    saveAs(
      blob,
      `${fileNamePrefix}-${formattedStartDate}-${formattedEndDate}.${fileExtension}`
    );
  } catch (error) {
    console.error(errorMessage, error);
    throw error; // Re-throw to allow caller to handle
  } finally {
    dispatch(setIsPrinting(false));
  }
};
